import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Paper, Stack, Typography, IconButton, Button } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { FieldArray, Form, Formik } from 'formik';
import * as Yup from 'yup';

import useNotification from '../../../../../core/hooks/use_notification';
import TextFieldFormik from '../../../../common/text_field';
import { SubmitButton } from '../../../../common/button';
import BottomActionBar from "../../../../common/bottom_action_bar";

import { getProductAction, getProductState } from '../../../../../features/product/get_product';
import { updateProductEansAction, updateProductEansState } from '../../../../../features/product/update_product_eans';
import { getProductNotificationData, updateProductEansNotificationData } from "../../../../../core/utils/notification_utils";
import { ApiRequestStatusEnum } from "../../../../../core/enums/common/api";

const ProductEansForm = ({ productId }) => {
    const dispatch = useDispatch();
    const { showNotification, showErrorMessage } = useNotification();

    const getProduct = useSelector(getProductState);
    const updateProductEans = useSelector(updateProductEansState);

    const initialValues = {
        eans: getProduct?.data?.eans || []
    };

    const schema = Yup.object().shape({
        eans: Yup.array().of(
            Yup.string().required('Kod EAN nie może być pusty')
        )
    });

    const onSubmit = (values) => {
        const payload = {
            productId,
            form: {
                eans: values.eans
            }
        };

        dispatch(updateProductEansAction(payload))
            .then(response => {
                showNotification(updateProductEansNotificationData(response));
                if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                    dispatch(getProductAction({ id: productId }))
                        .then(response => showErrorMessage(getProductNotificationData(response)));
                }
            })
    };

    return (
        <Formik
            initialValues={ initialValues }
            validationSchema={ schema }
            validateOnChange={ false }
            validateOnBlur={ false }
            enableReinitialize={ true }
            onSubmit={ onSubmit }
        >
            {({ values }) => (
                <Box component={ Form } sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, height: '100%' } }>
                    <Box sx={ { flexGrow: 1, mb: 4 } }>
                        <Paper variant="outlined" sx={ { p: 4 } }>
                            <Typography variant="subtitle2" color="textSecondary" sx={ { mb: 3, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.5px' } }>
                                Warianty / Kody EAN
                            </Typography>

                            <FieldArray name="eans">
                                {({ push, remove }) => (
                                    <Stack spacing={ 2 } alignItems="flex-start">
                                        {values.eans && values.eans.length > 0 ? (
                                            values.eans.map((ean, index) => (
                                                <Stack direction="row" spacing={ 2 } alignItems="flex-start" key={ index } sx={{ width: { xs: '100%', sm: '50%', md: '30%' } }}>
                                                    <Box sx={{ flexGrow: 1 }}>
                                                        <TextFieldFormik
                                                            name={ `eans.${index}` }
                                                            label={ `Kod EAN ${index + 1}` }
                                                            size="small"
                                                            required
                                                        />
                                                    </Box>
                                                    <IconButton
                                                        onClick={ () => remove(index) }
                                                        sx={{ mt: 0.5 }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Stack>
                                            ))
                                        ) : (
                                            <Typography variant="body2" color="textSecondary">
                                                Brak przypisanych kodów EAN do tego produktu.
                                            </Typography>
                                        )}

                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={ <AddIcon /> }
                                            onClick={ () => push('') }
                                            sx={{ mt: 2 }}
                                        >
                                            Dodaj kolejny kod
                                        </Button>
                                    </Stack>
                                )}
                            </FieldArray>

                        </Paper>
                    </Box>

                    <BottomActionBar>
                        <SubmitButton isLoading={ updateProductEans?.loading }>
                            Zapisz kody EAN
                        </SubmitButton>
                    </BottomActionBar>
                </Box>
            )}
        </Formik>
    );
};

export default ProductEansForm;